import { createPortal } from "react-dom";
import {
    Slider,
    Box,
    Typography,
    Paper,
    Tooltip,
    IconButton
} from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { DomEvent } from "leaflet";
import { setFilter } from "../../store/reducers/stateReducer";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { History } from "@mui/icons-material";

function formatDate(date) {
    if (!date) return null
    const datePart = new Date(date + 10800000).toISOString().split('T')[0]
    const [year, month, day] = datePart.split('-')
    return `${day}.${month}.${year}`
}

// Функция для сравнения массивов
const arraysEqual = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.length === b.length && a[0] === b[0] && a[1] === b[1];
}

const DateControl = () => {
    const dispatch = useDispatch()
    const firstRender = useRef(null)
    const electricity = useSelector(state => state.state.electricity)
    const { date: dateFilter, stage: stageFilter } = useSelector(state => state.state.filter);
    const [isMobile, setIsMobile] = useState(false)
    const [isOpen, setIsOpen] = useState(true)
    const [localFilter, setLocalFilter] = useState(null)
    const dateRef = useRef(null)
    const buttonRef = useRef(null);
    
    const marks = useMemo(() => {
        let hosts = [...electricity.pillars, ...electricity.fiders, ...electricity.pillarsOpn, ...electricity.pillarsZvz, ...electricity.pillarsRunn, ...electricity.pillarsRlnd]
        if (stageFilter && stageFilter.length > 0) {
            hosts = hosts.filter(h => stageFilter.includes(Number(h?.stage)))
        }
        const dates = [
            ...new Set(
                hosts.filter(h => h.date_history).map(p => new Date(p.date_history).getTime())
            )]
        return dates.sort((a, b) => a - b).map(d => {
            return {
                value: d,
            }
        })
    }, [electricity, stageFilter])

    const startEnd = useMemo(() => {
        if (!marks || !marks.length > 0) return null
        return [marks[0].value, marks[marks.length - 1].value]
    }, [marks])

    useEffect(() => {
        if (!firstRender.current) setLocalFilter(dateFilter)
        firstRender.current = true
    }, [dateFilter])

    useEffect(() => {
        if (!localFilter) setLocalFilter(startEnd)
    }, [startEnd])

    useEffect(() => {
        setLocalFilter(startEnd)
    }, [stageFilter])

    useEffect(() => {
        if (!localFilter || !startEnd) return;

        // Этот эффект обновляет Redux на основе локальных изменений.
        // Он также обрабатывает сброс фильтра при смене stage.
        const isFullRange = startEnd[0] === localFilter[0] && startEnd[1] === localFilter[1];

        // Если localFilter совпадает с полным диапазоном, 
        // то фильтр в Redux должен быть null.
        if (isFullRange) {
            if (dateFilter !== null) {
                dispatch(setFilter({ 'date': null }));
            }
            return; // Больше ничего не делаем
        }

        // Если дошли сюда, значит выбран диапазон.
        // Отправляем его в Redux с задержкой.
        const timer = setTimeout(() => {
            const filterData = {
                min: startEnd[0],
                max: startEnd[1],
                start: localFilter[0],
                end: localFilter[1]
            };
            // Отправляем, только если данные отличаются от тех, что уже в Redux
            if (dateFilter?.start !== filterData.start || dateFilter?.end !== filterData.end) {
                dispatch(setFilter({ 'date': filterData }));
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [localFilter])

    // Отключаем пропагацию кликов для Leaflet
    useEffect(() => {
        if (dateRef.current) {
            const leaflet_container = document.getElementsByClassName('leaflet-container')[0]
            if (leaflet_container) {
                DomEvent.disableClickPropagation(leaflet_container)
            }
        }
    }, [])

    useEffect(() => {
        if (buttonRef.current) {
            DomEvent.disableClickPropagation(buttonRef.current);
            DomEvent.disableScrollPropagation(buttonRef.current);
        }
    }, [buttonRef.current]);

    const handleSliderChange = useCallback((event, newValue) => {
        setLocalFilter(newValue);
    }, []);

    if (!marks || !startEnd || !localFilter || startEnd[0] === startEnd[1]) return null

    return (

        createPortal(
            <Box
                id="leaflet-date"
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    zIndex: 1300,
                    order: 0,
                    boxShadow: 'none',
                    position: 'absolute',
                    left: '50%',
                    pointerEvents: 'auto',
                    transform: 'translate(-50%, 0)',
                    '@media (max-width: 1600px)': {
                        position: 'relative',
                        left: 'auto',
                        transform: 'translate(0)',
                    }
                }}
            >
                <Tooltip title="История" placement="top" arrow>
                    <Paper
                        ref={buttonRef}
                        elevation={3}
                        sx={{
                            display: isOpen ? 'none' : 'flex',
                            order: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: '26px', md: '34px' },
                            height: { xs: '26px', md: '34px' },
                            // m: '10px 10px 5px 5px',
                            opacity: 0.9,
                            bgcolor: 'rgb(40, 40, 40)',
                            boxShadow: isOpen ? '0 0 3px 3px rgb(0, 174, 255)' : '0px 2px 6px grey',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover': {
                                bgcolor: 'rgb(50, 50, 50)',
                                transform: 'scale(1.1)',

                            },
                        }}
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <History sx={{ fontSize: { xs: '19px', md: '24px' } }} />
                    </Paper>
                </Tooltip>
                <Paper
                    ref={dateRef}
                    elevation={0}
                    sx={{
                        display: isOpen ? 'flex' : 'none',
                        zIndex: 3002,
                        fontSize: { xs: '9px', md: '14px' },
                        boxShadow: '0px 2px 6px grey',
                        borderRadius: '3px',
                        pointerEvents: 'auto',
                        color: 'white',
                        // border: '2px solid rgba(0, 0, 0, 0.2)',
                        borderSpacing: '5px',
                        width: { xs: '250px', md: '350px' },
                        height: { xs: '26px', md: '34px' },
                        padding: '0 5px',
                        backgroundColor: 'rgb(0, 0, 0)',
                        opacity: 0.7,
                        cursor: 'pointer'
                        // transform: window.innerWidth < 768 ? 'scale(0.7)' : null,
                        // transformOrigin: 'right top'
                        // zoom: window.innerWidth < 768 ? 0.8 : 1
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '3px', width: '100%' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: { xs: '0.7rem', md: '0.85rem' }
                            }}
                        >
                            {formatDate(localFilter[0])}
                        </Typography>

                        <Slider
                            style={{ margin: '10px', padding: '0px' }}
                            size="small"
                            orientation="horizontal"
                            value={localFilter}
                            // defaultValue={localFilter}
                            onChange={handleSliderChange}
                            // onChangeCommitted={updateFilter(localFilter)}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => { return formatDate(v) }}
                            marks={marks}
                            min={startEnd[0]}
                            max={startEnd[1]}
                            step={86400000}
                            sx={{
                                '& .MuiSlider-valueLabelOpen': {
                                    transform: 'rotate(180deg) translateY(-10px) !important',
                                },
                                '& .MuiSlider-valueLabelCircle': {
                                    transform: 'rotate(180deg) !important',
                                }
                            }}
                        />

                        <Typography
                            variant="body2"
                            sx={{
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: { xs: '0.7rem', md: '0.85rem' }
                            }}
                        >
                            {formatDate(localFilter[1])}
                        </Typography>
                        {window.innerWidth < 768 &&
                            <Tooltip title="Свернуть" placement="top" arrow>
                                <IconButton
                                    sx={{
                                        padding: 0,
                                        margin: 0,
                                        '&:hover': {
                                            bgcolor: 'rgb(50, 50, 50)',
                                            transform: 'scale(1.1)',
                                        },
                                    }}
                                    onClick={() => setIsOpen(false)}>
                                    <ExpandLessIcon sx={{ transform: 'rotate(90deg)', color: 'white', fontSize: '12px' }} />
                                </IconButton>
                            </Tooltip>
                        }
                    </Box>
                </Paper>
            </Box>,
            window.innerWidth < 1600
                ? document.getElementById("map-controls-right-top")
                : document.getElementById('map-controls-pane')
        )
    )
}

export default DateControl;